import { createSelector } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { buildNotesTableDataSource } from "@/entities/notes-table-v3";
import { currentPersonSelector } from "@/pages/ContactsPage/slice";
import type { RootState } from "@/services/store";
import {
  selectTitleFilter,
  selectNoteTitleFilter,
  selectFromDateFilter,
  selectToDateFilter,
  selectPersonGroupFilters,
  selectNoteTypeFilters,
  selectSorter,
  selectStringFilter,
  selectStatusFilters,
} from "./filters-slice";

const selectNotesState = (state: RootState) => state.contactIdeas.notes;

export const selectNotes = createSelector([selectNotesState], (state) => state.notes);

export const selectPersons = createSelector([selectNotesState], (state) => state.persons);

export const selectIsLoading = createSelector([selectNotesState], (state) => state.isLoading);

export const selectActiveNote = createSelector([selectNotesState], (state) => state.activeNote);

export const selectSwipedNote = createSelector([selectNotesState], (state) => state.swipedNote);

export const selectNotesData = createSelector([selectNotes, selectPersons], (notes, persons) => {
  return buildNotesTableDataSource({ notes, persons });
});

export const selectNotesTableData = createSelector(
  [
    selectNotesData,
    selectTitleFilter,
    selectNoteTitleFilter,
    selectFromDateFilter,
    selectToDateFilter,
    selectPersonGroupFilters,
    selectNoteTypeFilters,
    selectSorter,
    selectStringFilter,
    selectStatusFilters,
    currentPersonSelector,
  ],
  (
    notesData,
    titleFilter,
    noteTitleFilter,
    fromDateFilter,
    toDateFilter,
    personGroupFilters,
    noteTypeFilters,
    sorter,
    stringFilter,
    statusFilters,
    currentPerson,
  ) => {
    const filteredData = notesData.filter((item) => {
      const matchesTitle =
        !titleFilter || item.person?.title?.toLowerCase().includes(titleFilter.toLowerCase());
      const matchesNoteTitle =
        !noteTitleFilter ||
        (item.title?.toLowerCase() || "").includes(noteTitleFilter.toLowerCase());
      const matchesFromDate = !fromDateFilter || dayjs(item.noteDate).isSameOrAfter(fromDateFilter);
      const matchesToDate = !toDateFilter || dayjs(item.noteDate).isSameOrBefore(toDateFilter);
      const matchesPersonGroup =
        !personGroupFilters.length ||
        personGroupFilters.includes(item.person?.group?.toUpperCase() || "");
      const matchesNoteType =
        !noteTypeFilters.length || noteTypeFilters.includes(item.noteType.name);
      const matchesStringFilter =
        !stringFilter ||
        (item.title?.toLowerCase() || "").includes(stringFilter.toLowerCase()) ||
        (item.person?.firstName || "").toLowerCase().includes(stringFilter.toLowerCase()) ||
        (item.person?.lastName || "").toLowerCase().includes(stringFilter.toLowerCase()) ||
        (item.person?.title || "").toLowerCase().includes(stringFilter.toLowerCase()) ||
        (item.text || "").toLowerCase().includes(stringFilter.toLowerCase());

      const matchesStatusFilters =
        !statusFilters.length || statusFilters.includes(item.status?.toUpperCase() || "");

      const matchesPerson = currentPerson?.id === item.personId;

      return (
        matchesTitle &&
        matchesNoteTitle &&
        matchesFromDate &&
        matchesToDate &&
        matchesPersonGroup &&
        matchesNoteType &&
        matchesStringFilter &&
        matchesStatusFilters &&
        matchesPerson
      );
    });

    if (!sorter) return filteredData;

    return [...filteredData].sort((a, b) => {
      switch (sorter) {
        case "NAME_ASC":
          return (a.person?.firstName || "").localeCompare(b.person?.firstName || "");
        case "NAME_DESC":
          return (b.person?.firstName || "").localeCompare(a.person?.firstName || "");
        case "SECOND_NAME_ASC":
          return (a.person?.lastName || "").localeCompare(b.person?.lastName || "");
        case "SECOND_NAME_DESC":
          return (b.person?.lastName || "").localeCompare(a.person?.lastName || "");
        case "TITLE_ASC":
          return (a.person?.title || "").localeCompare(b.person?.title || "");
        case "TITLE_DESC":
          return (b.person?.title || "").localeCompare(a.person?.title || "");
        default:
          return 0;
      }
    });
  },
);

export const selectSelectedNotes = createSelector(
  [selectNotesState],
  (selectedNotesState) => selectedNotesState.selectedNotes,
);

export const selectSelectedIds = createSelector(
  [selectNotesState],
  (selectedNotesState) => selectedNotesState.selectedIds,
);

export const notesSelectors = {
  selectNotes,
  selectPersons,
  selectIsLoading,
  selectNotesTableData,
  selectSelectedNotes,
  selectSelectedIds,
  selectActiveNote,
  selectSwipedNote,
  selectNotesData,
};
